/**
 * BankingDetailsForm Component
 *
 * Controlled form for editing banking details (bank account, bank code, bank
 * name, IBAN, SWIFT). Shows inline format-validation warnings (non-blocking)
 * and a top-level alert banner when any warning is present. A single
 * `formError` (used for both the "at least one field" validation and any
 * save error) renders as both a field-level error on `bankAccount` and a
 * top-level error banner (suppressed when warnings are visible).
 *
 * Additional severity-aware `errors` from external validation (e.g. invoice
 * cross-field checks) bucket by `path` and render below the matching input
 * via `FieldErrorList`.
 *
 * Uses `useTranslation('modules.modals.editBankingDetails')` for all labels.
 */
import * as React from 'react';
import { AlertBanner } from '../../validation/AlertBanner';
import { ErrorEntry } from '../../../components/errors';
import { BankingEntry } from './BankingEntry';
import { useTranslation } from '../../../providers/UIProvider';
import type { ValidationIssue } from '../../../types';
import { IBankingDetails } from '@insio/shared-types';
import {
  VARIABLE_SYMBOL_MAX_LENGTH,
  CONSTANT_SYMBOL_MAX_LENGTH,
  SPECIFIC_SYMBOL_MAX_LENGTH
} from '@insio/banking';

/**
 * Full payload passed to onSave: the editable form fields plus the
 * read-only symbol fields (varSymb / constSymb / specSymb) that are
 * preserved from the original bankingDetails and must be round-tripped
 * back to the mutation so they are not accidentally cleared.
 */

export interface BankingDetailsFormProps {
  value: IBankingDetails;
  onFieldChange: (field: keyof IBankingDetails, value: string) => void;
  swiftWarning?: string | null;
  bankCodeWarning?: string | null;
  ibanWarning?: string | null;
  hasAnyWarning?: boolean;
  formError?: string | null;
  /** External validation issues — bucketed by `path` and shown below the matching field. */
  errors?: ValidationIssue[];
  disabled?: boolean;
  varSymbWarning?: string | null;
  constSymbWarning?: string | null;
  specSymbWarning?: string | null;
  /** Hide the payment symbols (variable, constant, specific) — for a bare bank account. */
  hideSymbols?: boolean;
  /** Prefix for field element ids, so several forms can share a page; ids are the field names without it. */
  idPrefix?: string;
}

const FIELD_KEYS = [
  'bankAccount',
  'bankCode',
  'bankName',
  'iban',
  'swift',
  'varSymb',
  'constSymb',
  'specSymb'
] as const;
type FieldKey = (typeof FIELD_KEYS)[number];

function bucketIssues(errors: ValidationIssue[] | undefined) {
  const byField: Record<FieldKey, ValidationIssue[]> = {
    bankAccount: [],
    bankCode: [],
    bankName: [],
    iban: [],
    swift: [],
    varSymb: [],
    constSymb: [],
    specSymb: []
  };
  const top: ValidationIssue[] = [];
  for (const issue of errors ?? []) {
    const path = issue.path ?? '';
    if ((FIELD_KEYS as readonly string[]).includes(path)) {
      byField[path as FieldKey].push(issue);
    } else {
      top.push(issue);
    }
  }
  return { byField, top };
}

export const BankingDetailsForm: React.FC<BankingDetailsFormProps> = ({
  value,
  onFieldChange,
  swiftWarning = null,
  bankCodeWarning = null,
  ibanWarning = null,
  varSymbWarning = null,
  constSymbWarning = null,
  specSymbWarning = null,
  hasAnyWarning = false,
  formError = null,
  errors,
  disabled = false,
  hideSymbols = false,
  idPrefix
}) => {
  const t = useTranslation('modules.modals.editBankingDetails');
  const { byField, top } = React.useMemo(() => bucketIssues(errors), [errors]);
  const idOf = (field: FieldKey) => (idPrefix ? `${idPrefix}-${field}` : field);

  return (
    <div className='bg-white rounded-lg border border-border-base p-md space-y-md'>
      {hasAnyWarning && (
        <AlertBanner
          variant='warning'
          title={t('warnings.invalidDataTitle')}
          body={t('warnings.invalidDataBody')}
        />
      )}

      {formError && !hasAnyWarning && <AlertBanner variant='error' title={formError} />}

      {top.map((issue, i) => (
        <AlertBanner
          key={`top-${i}`}
          variant={issue.severity === 'error' ? 'error' : 'warning'}
          title={<ErrorEntry issue={issue} showActual />}
        />
      ))}

      <div className='grid grid-cols-2 gap-md'>
        <div data-hover-field='bankingDetails.bankAccount'>
          <BankingEntry
            field='bankAccount'
            id={idOf('bankAccount')}
            label={t('fields.accountNumber')}
            value={value.bankAccount}
            onFieldChange={onFieldChange}
            placeholder='123456789/0100'
            warning={!hasAnyWarning ? formError : null}
            issues={byField.bankAccount}
            disabled={disabled}
          />
        </div>

        <div data-hover-field='bankingDetails.bankCode'>
          <BankingEntry
            field='bankCode'
            id={idOf('bankCode')}
            label={t('fields.bankCode')}
            value={value.bankCode}
            onFieldChange={onFieldChange}
            placeholder='0800'
            warning={bankCodeWarning}
            issues={byField.bankCode}
            disabled={disabled}
          />
        </div>
      </div>

      <div data-hover-field='bankingDetails.bankName'>
        <BankingEntry
          field='bankName'
          id={idOf('bankName')}
          label={t('fields.bankName')}
          value={value.bankName}
          onFieldChange={onFieldChange}
          placeholder='ČSOB'
          issues={byField.bankName}
          disabled={disabled}
        />
      </div>

      <div data-hover-field='bankingDetails.iban'>
        <BankingEntry
          field='iban'
          id={idOf('iban')}
          label={t('fields.iban')}
          value={value.iban}
          onFieldChange={onFieldChange}
          placeholder='CZ65 0800 0000 1920 0014 5399'
          warning={ibanWarning}
          issues={byField.iban}
          uppercase
          disabled={disabled}
        />
      </div>

      <div data-hover-field='bankingDetails.swift'>
        <BankingEntry
          field='swift'
          id={idOf('swift')}
          label={t('fields.swift')}
          value={value.swift}
          onFieldChange={onFieldChange}
          placeholder='GIBACZPX'
          warning={swiftWarning}
          issues={byField.swift}
          uppercase
          disabled={disabled}
        />
      </div>

      {!hideSymbols && (
        <>
          <div data-hover-field='bankingDetails.varSymb'>
            <BankingEntry
              field='varSymb'
              id={idOf('varSymb')}
              label={t('fields.varSymb')}
              value={value.varSymb}
              onFieldChange={onFieldChange}
              placeholder='123456789'
              warning={varSymbWarning}
              issues={byField.varSymb}
              numeric
              maxLength={VARIABLE_SYMBOL_MAX_LENGTH}
              disabled={disabled}
            />
          </div>

          <div data-hover-field='bankingDetails.constSymb'>
            <BankingEntry
              field='constSymb'
              id={idOf('constSymb')}
              label={t('fields.constSymb')}
              value={value.constSymb}
              onFieldChange={onFieldChange}
              placeholder='1234'
              warning={constSymbWarning}
              issues={byField.constSymb}
              numeric
              maxLength={CONSTANT_SYMBOL_MAX_LENGTH}
              disabled={disabled}
            />
          </div>

          <div data-hover-field='bankingDetails.specSymb'>
            <BankingEntry
              field='specSymb'
              id={idOf('specSymb')}
              label={t('fields.specSymb')}
              value={value.specSymb}
              onFieldChange={onFieldChange}
              placeholder='123456789'
              warning={specSymbWarning}
              issues={byField.specSymb}
              numeric
              maxLength={SPECIFIC_SYMBOL_MAX_LENGTH}
              disabled={disabled}
            />
          </div>
        </>
      )}
    </div>
  );
};

BankingDetailsForm.displayName = 'BankingDetailsForm';
